import React, { useEffect, useState } from "react";
import { Typography, Radio, Input, Textarea } from "@material-tailwind/react";

export interface AgentEvaluationFormData {
  arrived30To60MinutesEarly?: boolean | null;
  testified?: boolean | null;
  confessed?: boolean | null;
  confessionDetails?: string;
  overallRating?: number; // 0-10
  ratingJustification?: string;
  confidentAboutCaseInfo?: boolean | null;
  otherRelevantInfo?: string;
}

interface Props {
  onChange: (data: { isValid: boolean; formattedData: AgentEvaluationFormData }) => void;
}

type YesNoNa = "Sim" | "Não" | "Não se aplica" | null;

const toBooleanOrNull = (value: YesNoNa): boolean | null => {
  if (value === "Sim") return true;
  if (value === "Não") return false;
  return null; // "Não se aplica"
};

const AgentEvaluationForm: React.FC<Props> = ({ onChange }) => {
  const [form, setForm] = useState<{
    arrived30To60MinutesEarly: YesNoNa;
    testified: YesNoNa;
    confessed: YesNoNa;
    confessionDetails: string;
    overallRating: string; // "0"-"10"
    ratingJustification: string;
    confidentAboutCaseInfo: YesNoNa;
    otherRelevantInfo: string;
  }>({
    arrived30To60MinutesEarly: null,
    testified: null,
    confessed: null,
    confessionDetails: "",
    overallRating: "",
    ratingJustification: "",
    confidentAboutCaseInfo: null,
    otherRelevantInfo: "",
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isValid, setIsValid] = useState(false);

  const required = [
    "arrived30To60MinutesEarly",
    "testified",
    "confessed",
    "overallRating",
    "confidentAboutCaseInfo",
  ];

  const ratingBelowSeven =
    form.overallRating !== "" && Number(form.overallRating) < 7;

  useEffect(() => {
    const ok = required.every((k) => !!(form as any)[k] || (k === "overallRating" && form.overallRating !== ""));
    // Se confessed = "Sim", confessionDetails também é obrigatório
    const confessionDetailsRequired = form.confessed === "Sim" && form.confessionDetails.trim() === "";
    // Nota abaixo de 7 exige justificativa
    const justificationRequired = ratingBelowSeven && form.ratingJustification.trim() === "";
    setIsValid(ok && !confessionDetailsRequired && !justificationRequired);
  }, [form]);

  useEffect(() => {
    onChange({
      isValid,
      formattedData: {
        arrived30To60MinutesEarly: toBooleanOrNull(form.arrived30To60MinutesEarly),
        testified: toBooleanOrNull(form.testified),
        confessed: toBooleanOrNull(form.confessed),
        confessionDetails: form.confessed === "Sim" ? form.confessionDetails?.trim() || undefined : undefined,
        overallRating: form.overallRating === "" ? undefined : Number(form.overallRating),
        ratingJustification: form.ratingJustification?.trim() || undefined,
        confidentAboutCaseInfo: toBooleanOrNull(form.confidentAboutCaseInfo),
        otherRelevantInfo: form.otherRelevantInfo?.trim() || undefined,
      },
    });
  }, [form, isValid, onChange]);

  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));
  const isInvalid = (name: string) => touched[name] && !((form as any)[name] || (name === "overallRating" && form.overallRating !== ""));

  const yesNoField = (
    name: keyof typeof form,
    label: string,
  ) => (
    <div>
      <Typography variant="small" className="text-blue-700 font-medium mb-1">
        {label} <span className="text-red-500">*</span>
      </Typography>
      <div className="flex gap-4 flex-wrap">
        {(["Sim", "Não", "Não se aplica"] as const).map((option) => (
          <Radio
            key={option}
            name={name}
            label={option}
            checked={(form as any)[name] === option}
            onChange={() => {
              markTouched(String(name));
              setForm((s) => ({ ...s, [name]: option }));
            }}
          />
        ))}
      </div>
      {isInvalid(String(name)) && (
        <Typography className="text-red-500 text-xs mt-1">Este campo é obrigatório</Typography>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {yesNoField("arrived30To60MinutesEarly", "O preposto chegou com 30 a 60 minutos de antecedência?")}
      {yesNoField("testified", "O preposto prestou depoimento?")}
      {yesNoField("confessed", "Houve confissão do preposto?")}
      
      {/* Campo condicional para detalhes da confissão */}
      {form.confessed === "Sim" && (
        <div>
          <Typography variant="small" className="text-blue-700 font-medium mb-1">
            Descreva detalhes sobre a confissão <span className="text-red-500">*</span>
          </Typography>
          <Textarea
            name="confessionDetails"
            value={form.confessionDetails}
            onChange={(e) => {
              markTouched("confessionDetails");
              setForm((s) => ({ ...s, confessionDetails: e.target.value }));
            }}
            className={`w-full ${form.confessed === "Sim" && touched.confessionDetails && form.confessionDetails.trim() === "" ? "border-red-500" : ""}`}
            placeholder="Descreva os detalhes da confissão do preposto"
          />
          {form.confessed === "Sim" && touched.confessionDetails && form.confessionDetails.trim() === "" && (
            <Typography className="text-red-500 text-xs mt-1">Este campo é obrigatório quando há confissão</Typography>
          )}
        </div>
      )}
      
      {yesNoField("confidentAboutCaseInfo", "O preposto demonstrou estar seguro sobre as informações do caso?")}

      <div>
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          Nota geral da atuação do preposto (0 a 10) <span className="text-red-500">*</span>
        </Typography>
        <Input
          type="number"
          name="overallRating"
          value={form.overallRating}
          onChange={(e) => {
            markTouched("overallRating");
            let v = e.target.value;
            if (v === "") return setForm((s) => ({ ...s, overallRating: "" }));
            const n = Math.max(0, Math.min(10, Number(v)));
            setForm((s) => ({ ...s, overallRating: String(n) }));
          }}
          className={`w-full ${isInvalid("overallRating") ? "border-red-500" : ""}`}
          min={0}
          max={10}
          step={1}
        />
      </div>

      <div>
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          {ratingBelowSeven ? (
            <>
              Justificativa da nota <span className="text-red-500">*</span>
            </>
          ) : (
            "Justificativa da nota (opcional)"
          )}
        </Typography>
        <Textarea
          name="ratingJustification"
          value={form.ratingJustification}
          onChange={(e) => {
            markTouched("ratingJustification");
            setForm((s) => ({ ...s, ratingJustification: e.target.value }));
          }}
          className={`w-full ${ratingBelowSeven && touched.ratingJustification && form.ratingJustification.trim() === "" ? "border-red-500" : ""}`}
          placeholder="Explique brevemente os motivos da nota atribuída"
        />
        {ratingBelowSeven && touched.ratingJustification && form.ratingJustification.trim() === "" && (
          <Typography className="text-red-500 text-xs mt-1">
            Este campo é obrigatório para notas abaixo de 7
          </Typography>
        )}
      </div>

      <div>
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          Outras informações relevantes (opcional)
        </Typography>
        <Textarea
          name="otherRelevantInfo"
          value={form.otherRelevantInfo}
          onChange={(e) => setForm((s) => ({ ...s, otherRelevantInfo: e.target.value }))}
          className="w-full"
          placeholder="Inclua quaisquer outras observações que considere importantes"
        />
      </div>
    </div>
  );
};

export default AgentEvaluationForm;
